import React, { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { withRouter } from 'react-router-dom'

import SearchParamBuilder from '../../higher-order/searchable-table/search-param-builder'
import PageParamBuilder from '../../higher-order/pageable-table/page-param-builder'
import { ActiveInfo } from '../../models/activeInfo'
import { ChainTypes } from '../../models/chainTypes'
import {
  getUsers,
  getUserRoles,
  exportStoreUsers,
  exportRepresentativeUsers,
} from '../../store/actions/users-actions'
import { getRepresentative } from '../../store/actions/store-info-actions'
import ExportUserModal from './UI/ExportUserModal'
import UsersUI from './UI/UsersUI'

const Users = props => {
  const [pageInfo, setPageInfo] = useState(
    Object.assign(new PageParamBuilder(), {
      page: props.pagination.pageNumber,
      size: props.pagination.size,
      count: props.pagination.totalPages,
      sortList:
        props.lastSort && props.lastSort.headerName !== ''
          ? [`${props.lastSort.headerName},${props.lastSort.direction}`]
          : ['username,asc'],
    }),
  )
  const [searchInfo, setSearchInfo] = useState(
    Object.assign(new SearchParamBuilder(), props.searchInfo),
  )
  const [activeInfo, setActiveInfo] = useState(
    Object.assign(new ActiveInfo(), props.activeInfo),
  )
  const [lastSort, setLastSort] = useState({
    headerName: props.lastSort.headerName || '',
    direction: props.lastSort.direction || '',
  })
  const [activeFilter, setActiveFilter] = useState(
    props.activeInfo.active ? 'active' : 'inactive',
  )

  useEffect(() => {
    props.getUserRoles()
    props.getRepresentative(props.currentRepresentativeId)
  }, [])

  useEffect(() => {
    setSearchInfo(Object.assign(new SearchParamBuilder(), props.searchInfo))
  }, [props.searchInfo])

  useEffect(() => {
    setPageInfo(
      Object.assign(new PageParamBuilder(), {
        page: props.pagination.pageNumber,
        size: props.pagination.size,
        totalPages: props.pagination.totalPages,
        sortList:
          props.lastSort && props.lastSort.headerName !== ''
            ? [`${props.lastSort.headerName},${props.lastSort.direction}`]
            : [],
      }),
    )
  }, [props.pagination])

  useEffect(() => {
    setActiveInfo(Object.assign(new ActiveInfo(), props.activeInfo))
    setActiveFilter(props.activeInfo.active ? 'active' : 'inactive')
  }, [props.activeInfo])

  useEffect(() => {
    setLastSort(props.lastSort)
  }, [props.lastSort])

  useEffect(() => {
    setSearchInfo(
      Object.assign(new SearchParamBuilder(), {
        search: props.searchInfo.search,
      }),
    )
  }, [props.searchInfo])

  const handleToggleActive = activeFilterValue => {
    setPageInfo(Object.assign(new PageParamBuilder(), { ...pageInfo, page: 0 }))
    const activeInfoNew = Object.assign(new ActiveInfo(), {
      active: activeFilterValue === 'active',
    })
    setActiveInfo(activeInfoNew)
    setActiveFilter(activeFilterValue)
    props.getUsers(pageInfo, searchInfo, activeInfoNew)
  }

  const handleLoadPageUsers = newPageInfo => {
    setPageInfo(newPageInfo)
    props.getUsers(newPageInfo, searchInfo, activeInfo)
  }

  const handleLoadSearchUsers = newSearchInfo => {
    setPageInfo(Object.assign(new PageParamBuilder(), { ...pageInfo, page: 0 }))
    setSearchInfo(newSearchInfo)
    props.getUsers(pageInfo, newSearchInfo, activeInfo)
  }

  const handleNavigateToDetailView = id => {
    props.history.push(`/user-details/${id}`)
  }

  const [showExportModal, setShowExportModal] = useState(false)
  const [exportFile, setExportFile] = useState(null)

  const handleCloseExportModal = () => {
    if (exportFile?.url) {
      URL.revokeObjectURL(exportFile.url)
    }
    setShowExportModal(false)
    setExportFile(null)
  }

  const handleStoreUserExport = () => {
    setShowExportModal(true)
    props.exportStoreUsers().then(setExportFile).catch(handleCloseExportModal)
  }

  const handleRepresentativeUserExport = () => {
    setShowExportModal(true)
    props
      .exportRepresentativeUsers()
      .then(setExportFile)
      .catch(handleCloseExportModal)
  }

  const isSingleOwnerOrFranchise = useMemo(
    () =>
      [ChainTypes.SINGLE_OWNER, ChainTypes.FRANCHISEE].includes(
        props.currentRepresentative.chainType,
      ),
    [props?.currentRepresentativeId?.chainType],
  )

  return (
    <>
      <UsersUI
        users={props.users}
        activeFilter={activeFilter}
        pagination={props.pagination}
        lastSort={lastSort}
        searchTerm={searchInfo.search}
        isSingleOwnerOrFranchise={isSingleOwnerOrFranchise}
        onToggleActive={active => handleToggleActive(active)}
        onLoadPagingData={newPageInfo => handleLoadPageUsers(newPageInfo)}
        onLoadSearchData={newSearchInfo => handleLoadSearchUsers(newSearchInfo)}
        onNavigateToDetailView={id => handleNavigateToDetailView(id)}
        onExportStoreUsers={handleStoreUserExport}
        onExportRepresentativeUsers={handleRepresentativeUserExport}
      />
      <ExportUserModal
        show={showExportModal}
        file={exportFile}
        onClose={handleCloseExportModal}
      />
    </>
  )
}

Users.propTypes = {
  users: PropTypes.object.isRequired,
  pagination: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  searchInfo: PropTypes.object,
  activeInfo: PropTypes.object,
  lastSort: PropTypes.object,
  currentRepresentativeId: PropTypes.string,

  getUsers: PropTypes.func.isRequired,
  getUserRoles: PropTypes.func.isRequired,
  getRepresentative: PropTypes.func.isRequired,
}

Users.defaultProps = {
  searchInfo: {
    search: '',
  },
  activeInfo: {
    active: true,
  },
  lastSort: {
    headerName: '',
    direction: '',
  },
  currentRepresentativeId: '',
}

const mapStateToProps = state => ({
  users: state.users,
  searchInfo: state.users.searchInfo,
  pagination: state.users.pagination,
  activeInfo: state.users.activeInfo,
  lastSort: state.users.lastSort,
  currentRepresentative: state.storeInfo.currentRepresentative,
  currentRepresentativeId: state.auth.representative.representativeUID,
})

const mapDispatchToProps = dispatch => ({
  getUsers: (pageInfo, searchInfo, activeInfo) => {
    dispatch(getUsers(pageInfo, searchInfo, activeInfo))
  },
  getUserRoles: () => {
    dispatch(getUserRoles())
  },
  getRepresentative: repId => {
    dispatch(getRepresentative(repId))
  },
  exportStoreUsers: () => dispatch(exportStoreUsers()),
  exportRepresentativeUsers: () => dispatch(exportRepresentativeUsers()),
})

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Users))
